import { useState, type FormEvent } from "react";
import type { QuotaResponse } from "../../../../shared/contracts/billing";

type QuotaPanelProps = {
  quota: QuotaResponse | null;
  loading?: boolean;
  onRedeem: (code: string, confirmRestore?: boolean) => Promise<unknown> | void;
};

function errorCode(error: unknown): string | null {
  if (typeof error !== "object" || error === null || !("code" in error)) return null;
  const code = (error as { code?: unknown }).code;
  return typeof code === "string" ? code : null;
}

export function QuotaPanel({ quota, loading = false, onRedeem }: QuotaPanelProps) {
  const [code, setCode] = useState("");
  const [restoreConfirmation, setRestoreConfirmation] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function redeem(confirmRestore = false) {
    const normalized = code.trim();
    if (!normalized) return;
    setSubmitting(true);
    setError(null);
    try {
      if (confirmRestore) await onRedeem(normalized, true);
      else await onRedeem(normalized);
      setCode("");
      setRestoreConfirmation(false);
    } catch (reason) {
      if (errorCode(reason) === "restore_confirmation_required") {
        setRestoreConfirmation(true);
      } else {
        setError("兑换暂时不可用，请检查兑换码后重试。");
      }
    } finally {
      setSubmitting(false);
    }
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    void redeem();
  }

  return (
    <section className="quota-panel" aria-labelledby="quota-heading">
      <div className="section-heading">
        <div>
          <p className="eyebrow">查询额度</p>
          <h2 id="quota-heading">今日可用次数</h2>
        </div>
        {loading && !quota ? <span className="muted-text">正在读取…</span> : null}
      </div>
      <div className="quota-counts" aria-live="polite">
        <span>{quota ? `今日免费 ${quota.freeRemaining} 次` : "今日免费 —"}</span>
        <span>{quota ? `付费剩余 ${quota.paidRemaining} 次` : "付费剩余 —"}</span>
      </div>
      <details className="redeem-details">
        <summary>使用兑换码</summary>
        <form className="redeem-row" onSubmit={submit}>
          <label htmlFor="redeem-code">兑换码</label>
          <input
            id="redeem-code"
            type="text"
            value={code}
            onChange={(event) => {
              setCode(event.target.value);
              setRestoreConfirmation(false);
              setError(null);
            }}
            placeholder="输入测试兑换码"
            autoComplete="off"
          />
          <button className="secondary-button" type="submit" disabled={submitting || code.trim().length === 0}>
            {submitting ? "处理中…" : "兑换"}
          </button>
        </form>
        {restoreConfirmation ? (
          <div className="quota-confirmation" role="alert">
            <p>将切换到兑换码原钱包；当前钱包余额不会合并或迁移。</p>
            <button className="secondary-button" type="button" disabled={submitting} onClick={() => void redeem(true)}>
              确认切换并恢复
            </button>
          </div>
        ) : null}
        {error ? <p className="quota-error" role="alert">{error}</p> : null}
      </details>
    </section>
  );
}
